import React, { Fragment, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import qs from 'query-string';
import Helmet from 'react-helmet';
import { Formik } from 'formik';
import { useMutation, useQuery } from 'react-apollo';

import MarcomLayout from '../../../components/global/MarcomLayout';
import SignupAlbumLayout from '../../../components/global/SignupAlbumLayout';
import FullScreenLoader from '../../../components/global/FullScreenLoader';
import ErrorView from '../../../components/global/ErrorView';
import PasswordSetupStep from './components/PasswordSetupStep';
import InvitationAcceptSuccessStep from './components/InvitationAcceptSuccessStep';
import useOpenAlert from '../../../hooks/useOpenAlert';
import { teamMember as teamMemberQuery } from '../../../graphql/queries/team_members';
import { teamMemberInvitationAccept as teamMemberInvitationAcceptMutation } from '../../../graphql/mutations/team-members';
import { TEAM_MEMBER_STATUS } from '../../../constants/team-members';
import { getErrorString } from '../../../util/normalize-errors';

const TeamMemberInvitationAcceptView = () => {
  const location = useLocation();
  const openAlert = useOpenAlert();
  const [invitationAccepted, setInvitationAccepted] = useState(false);
  const { teamMemberId, token } = qs.parse(location.search ?? '');

  const { data: { teamMember } = {}, loading: queryLoading } = useQuery(
    teamMemberQuery,
    {
      variables: { team_member_id: teamMemberId, token },
      skip: !teamMemberId || !token,
    },
  );

  const [teamMemberInvitationAccept, { loading: mutationLoading }] =
    useMutation(teamMemberInvitationAcceptMutation);

  const onSubmit = async values => {
    if (!values.password || values.password.length < 8) {
      openAlert({
        message: 'Password must be at least 8 characters long',
      });
      return;
    }
    if (values.password !== values.passwordConfirmation) {
      openAlert({
        message: 'Passwords do not match',
      });
      return;
    }
    try {
      await teamMemberInvitationAccept({
        variables: {
          input: {
            team_member_id: teamMemberId,
            token,
            password: values.password,
          },
        },
      });
      setInvitationAccepted(true);
    } catch (e) {
      openAlert({
        message: 'There was an error accepting the invitation',
        info: getErrorString(e),
      });
    }
  };

  if (queryLoading) {
    return <FullScreenLoader />;
  }

  if (!teamMember) {
    return (
      <ErrorView
        location={{
          state: {
            error:
              'It looks like the invitation link provided does not match any of our records.',
          },
        }}
      />
    );
  }

  const teamUsername = teamMember.org_account?.username;
  const loginPath = teamUsername
    ? `/shutterbug/signin?defaultUserMode=TEAM&team=${teamUsername}`
    : '/shutterbug/signin?defaultUserMode=TEAM';

  if (invitationAccepted) {
    return (
      <Fragment>
        <Helmet>
          <title>Waldo &ndash; Invitation Accepted</title>
        </Helmet>
        <div className="team-member-invitation-success-wrapper">
          <SignupAlbumLayout.Wrapper
            imageDisabled
            logoSrc={null}
            withCTA={false}
            className="team-member-invitation-success-layout"
          >
            <InvitationAcceptSuccessStep loginPath={loginPath} />
          </SignupAlbumLayout.Wrapper>
        </div>
      </Fragment>
    );
  }

  if (teamMember.status === TEAM_MEMBER_STATUS.ACCEPTED) {
    return (
      <Fragment>
        <Helmet>
          <title>Waldo &ndash; Team Invitation</title>
        </Helmet>

        <MarcomLayout.Wrapper title="Welcome to the team!" signupDisabled>
          <p className="text-center text-muted mb-4">
            This invite has already been accepted and configured.
          </p>
          <div className="text-center">
            <Link to={loginPath} className="btn btn-primary">
              Go to login
            </Link>
          </div>
        </MarcomLayout.Wrapper>
      </Fragment>
    );
  }

  return (
    <Fragment>
      <Helmet>
        <title>Waldo &ndash; Accept Team Invitation</title>
      </Helmet>
      <MarcomLayout.Wrapper
        title={
          <>
            You&apos;re almost a part of the team{' '}
            <span className="text-primary">{teamUsername}</span>
          </>
        }
        signupDisabled
      >
        <Formik
          onSubmit={onSubmit}
          initialValues={{ password: '', passwordConfirmation: '' }}
          render={({ values, handleSubmit, handleChange }) => (
            <PasswordSetupStep
              loading={mutationLoading}
              handleSubmit={handleSubmit}
              handleChange={handleChange}
              helperText="Once your password is set, you will be able to log in to your team account via Team Login by using the team identifier and your new password."
              values={values}
            />
          )}
        />
      </MarcomLayout.Wrapper>
    </Fragment>
  );
};

export default TeamMemberInvitationAcceptView;
